import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_URL } from '../../config/api';
import { Camera, Calendar, ChevronDown, ChevronUp, Loader2, CheckCircle2 } from 'lucide-react';

const PhotoGallery = () => {
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const month = searchParams.get('month');
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedVisit, setExpandedVisit] = useState(null);

  useEffect(() => {
    loadGallery();
  }, [siteId, token, month]);

  const loadGallery = async () => {
    if (!token) { setError('Missing authentication'); setLoading(false); return; }
    try {
      const url = `${API_URL}/api/worker/gallery/${siteId}?token=${token}${month ? `&month=${month}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) { setError('Access denied or expired link'); setLoading(false); return; }
      const data = await res.json();
      setGallery(data.gallery || []);
    } catch { setError('Connection error'); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#1a1f4e]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center"><p className="text-red-600 font-medium">{error}</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#1a1f4e] text-white px-4 py-5">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm text-white/60">Just Cleaned</p>
          <h1 className="text-xl font-bold">Cleaning Photo Evidence</h1>
          {month && <p className="text-sm text-white/70 mt-1">{new Date(month + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {gallery.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No cleaning visits recorded for this period.</p>
          </div>
        ) : gallery.map(({ visit, before_photos, after_photos }) => {
          const isExpanded = expandedVisit === visit.id;
          return (
            <div key={visit.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setExpandedVisit(isExpanded ? null : visit.id)}
                className="w-full text-left p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-gray-900">{formatDate(visit.date)}</p>
                  </div>
                  <p className="text-sm text-gray-500">
                    {visit.worker_name} &middot; {formatTime(visit.started_at)} - {formatTime(visit.finished_at)}
                    &middot; {before_photos.length} before, {after_photos.length} after
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
                  {before_photos.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-2">Before Cleaning</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {before_photos.map(p => (
                          <img
                            key={p.id}
                            src={`${API_URL}/api/worker/photos/${p.storage_path}?auth=${token}`}
                            alt="Before"
                            className="w-full aspect-square object-cover rounded-lg border border-gray-200"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {after_photos.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-2">After Cleaning</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {after_photos.map(p => (
                          <img
                            key={p.id}
                            src={`${API_URL}/api/worker/photos/${p.storage_path}?auth=${token}`}
                            alt="After"
                            className="w-full aspect-square object-cover rounded-lg border border-gray-200"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PhotoGallery;
